import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys from 'camelcase-keys';

import { Database, Unit, UserPreferredUnit, DisplayUnit, withLogs, convertUnit } from '@core';

/**
 * @property {number} [outerMin] - The outer minimum margin. Yellow confidence
 * @property {number} [innerMin] - The inner minimum margin. Green confidence
 * @property {number} [innerMax] - The inner maximum margin. Green confidence
 * @property {number} [outerMax] - The outer maximum margin. Yellow confidence
 * @property {CorridorsDisplayUnit} [unit] - The unit for displaying corridors if the number is in inches we need to display fraction &frac|number|;.
 */
export type CorridorParameterMargins = {
    outerMin?: number;
    innerMin: number;
    innerMax: number;
    outerMax?: number;
    unit: DisplayUnit;
};

type ServerCorridor = {
    inner_min: number;
    inner_max: number;
    swing_measured_parameters: {
        name: string;
        units: {
            notation: string;
        } | null;
    };
};

type ServerCorridors = ServerCorridor[];

export type CorridorParameterMarginsMap = {
    [key: string]: CorridorParameterMargins;
};

export const mapCorridorsTable = (
    corridors: ServerCorridors,
    userPreferredUnit: UserPreferredUnit,
): CorridorParameterMarginsMap | undefined => {
    if (!corridors) return;

    return corridors.reduce((acc, corridor) => {
        const { swing_measured_parameters, ...rest } = corridor;
        const key = swing_measured_parameters.name;
        const unit = swing_measured_parameters.units?.notation as Unit;

        // we can return ratio right away
        // The statement above is wrong, we should multiply by a 100, the server ratio is a fraction not an integer number
        const innerMax = convertUnit({ unit, value: rest.inner_max, userPreferredUnit });
        const innerMin = convertUnit({ unit, value: rest.inner_min, userPreferredUnit });

        acc[key] = {
            innerMax: innerMax.value,
            innerMin: innerMin.value,
            unit: innerMax.unit,
        };

        return acc;
    }, {} as CorridorParameterMarginsMap);
};

export const getAllCorridors = withLogs(
    async (
        supabase: SupabaseClient<Database>,
        userPreferredUnit: UserPreferredUnit = 'metric',
    ): Promise<CorridorParameterMarginsMap> => {
        const { data, error } = await supabase
            .from('corridors')
            .select('inner_min, inner_max, swing_measured_parameters(name, units (notation))');

        if (error) {
            throw error;
        }

        if (!data || !data.length) {
            throw Error('corridors failed loading');
        }
        return mapCorridorsTable(data, userPreferredUnit) as CorridorParameterMarginsMap;
    },
    'getAllCorridors',
);

export const getAllCorridorsV2 = withLogs(async (supabase: SupabaseClient<Database>) => {
    const { data, error } = await supabase
        .from('corridors')
        .select('inner_min, inner_max, swing_measured_parameters(id, name, units (notation))');

    if (error) {
        throw error;
    }

    if (!data || !data.length) {
        throw Error('corridors failed loading');
    }
    return camelcaseKeys(data, { deep: true });
}, 'getAllCorridors');
